import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  MailTwoTone,
  MobileTwoTone,
  ArrowRightOutlined,
  RightOutlined,
} from '@ant-design/icons';
import { Button } from '@mm/components';
import { useResolutionInfo } from '@mm/hooks';
import { MenuIcon } from '@mm/icons';
import { useAutomationsCRUDActions } from '@mm/state/actions/automationActions';
import { useFacebookErrorCRUDActions } from '@mm/state/actions/FacebookErrorCRUDActions';
import { useLiveChatCRUDActions } from '@mm/state/actions/LiveChatCRUDActions';
import JSONUtils from '@mm/state/helpers/json-utils';
import { preferredConversationName } from '@mm/state/helpers/live-chat-helpers';
import { LivechatConversationModel } from '@mm/state/model/LivechatConversationModel';
import { getAutomationsAggregateStats } from '@mm/state/selectors/automations';
import { activeFacebookPageSelector } from '@mm/state/selectors/facebookPageSelector';
import { activeChatbotIdSelector } from '@mm/state/selectors/viewSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import noProfilePicIcon from 'assets/icons/contact_noimage.svg';
import { ReactComponent as DonkeyIcon } from 'assets/icons/dashboard-donkey.svg';
import { ReactComponent as LeftUnicornIcon } from 'assets/icons/dashboard-unicorn-left.svg';
import { ReactComponent as RightUnicornIcon } from 'assets/icons/dasboard-unicorn-right.svg';
import { ReactComponent as EmptyListIcon } from 'assets/icons/empty-list-icon.svg';
import classNames from 'classnames';
import moment from 'moment';
import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useContextSelector } from 'use-context-selector';
import { FacebookPageErrors } from '../../../../../../common/components/FacebookPageAlerts/FacebookPageErrors';
import { ImageWithFallback } from '../../../../../../common/components/ImageWithFallback/ImageWithFallback';
import { CHATBOT_EDITOR } from '../../../../../../common/constants/routes';
import ObjectUtils from '../../../../../../common/helpers/utils/object-utils';
import { BotBuilderRoutes } from '../../../../../../routes/bot-builder-routes';
import { messageStructureParser } from '../../../../liveChat/components/Messages/messageStructureParser';
import { LiveChatContext } from '../../../../liveChat/LiveChatContext';
import { ChannelsBlock } from '../ChannelsBlock/ChannelsBlock';
import { EducationalCardsBlock } from '../EducationalCardsBlock/EducationalCardsBlock';
import openLinkIcon from '../ChannelsBlock/Icons/open-link-icon.svg';
import css from './AutomationsDashboard.module.css';
import { FollowUpChats } from '../../../../../../layout/BotBuilderSidebar/common/TopLevelSidebarLink/FollowUpChats';
import { IAggregateAutomationStats } from '@mm/state/model/AutomationModel';

const FunnelDataBlock = ({
  percentage,
  count,
  name,
}: {
  percentage?: number;
  count: number;
  name: string;
}) => {
  return (
    <div className={css.funnelDataBlock}>
      {percentage !== undefined && (
        <div className={css.funnelDataBlockPercentage}>
          {percentage.toLocaleString()}%
        </div>
      )}
      <div className={css.funnelDataBlockCount}>
        {count.toLocaleString(undefined)}
      </div>
      <div className={css.funnelDataBlockName}>{name}</div>
    </div>
  );
};

const FinishedStats = ({
  aggregateStats,
}: {
  aggregateStats: IAggregateAutomationStats;
}) => {
  return (
    <div className={css.finishedContainer}>
      <div className={css.finishedContainerRowOne}>
        <div className={css.finishedCount}>{aggregateStats.finishedCount}</div>
        <div className={css.finishedPercentage}>
          {aggregateStats.contactsCount
            ? `${Math.round(
                ((aggregateStats.finishedCount || 0) * 100) /
                  aggregateStats.contactsCount,
              )}%`
            : undefined}
        </div>
      </div>
      <div className={css.finishedContainerRowTwo}>Finished</div>
    </div>
  );
};

export const NewDashboard = () => {
  const livechatCrudActions = useLiveChatCRUDActions();

  const activeChatbotId = useSelector(activeChatbotIdSelector);

  const activeFacebookPage = useSelector(activeFacebookPageSelector);

  const baseLcUrl = `${CHATBOT_EDITOR}/${activeChatbotId}/live-chat`;

  const remotePageId = useSelector(
    (state: StoreState) => state.connectedPage.pageDetails?.remoteId || '',
  );

  const { screenWidth } = useResolutionInfo();

  const [isFunnelExpanded, setIsFunnelExpanded] = useState(false);

  const { topArrowsCount } = useMemo(() => {
    const topArrowsCount =
      screenWidth < 1030
        ? 0
        : screenWidth < 1300
        ? 1
        : screenWidth < 1600
        ? 3
        : screenWidth < 1800
        ? 4
        : 5;

    return {
      topArrowsCount,
    };
  }, [screenWidth]);

  const [filteredConversations, setFilteredConversations] = useState<
    LivechatConversationModel[]
  >([]);

  const [valuableConversationsCount, setValuableConversationsCount] =
    useState(0);

  // Note: initial placeholder for future functinality of multiple lead types
  const [selectedTab, setSelectedTab] = useState(1);
  const qualifiedLeadsTabSelected = selectedTab === 1;

  const history = useHistory();

  const lcService = useContextSelector(
    LiveChatContext,
    v => v.lcServiceRef?.current,
  );

  const lcMachineSend = lcService?.send;

  const automationsCRUDActions = useAutomationsCRUDActions();
  const facebookErrorCRUDActions = useFacebookErrorCRUDActions();

  useEffect(() => {
    if (activeFacebookPage?.id) {
      facebookErrorCRUDActions.retrieveAll(activeFacebookPage.id);
    }
  }, [activeFacebookPage?.id]);

  const aggregateStats = useSelector(getAutomationsAggregateStats);

  useEffect(() => {
    if (activeChatbotId) {
      automationsCRUDActions.retrieveAggregateStatistics(activeChatbotId);
    }
  }, [activeChatbotId]);

  const params = {
    remotePageId,
    conversationState: 'all' as const,
    selectedTags: qualifiedLeadsTabSelected ? ['unicorn'] : [],
    page: 0,
    perPage: 6,
  };

  const getConversations = async params => {
    const res = await livechatCrudActions.getFilteredConversations(params);

    setFilteredConversations(res || []);
  };

  const getContactsCount = async params => {
    const res = await livechatCrudActions.getFilteredContactsCount(params);
    if (qualifiedLeadsTabSelected) {
      setValuableConversationsCount(res || 0);
    }
  };

  useEffect(() => {
    getConversations(params);
    getContactsCount(params);
  }, [remotePageId, selectedTab]);

  const seeValuableConversations = async () => {
    lcMachineSend?.({
      type: 'LC_VIEW_UPDATE',
      payload: {
        viewUpdate: {
          selectedTags: qualifiedLeadsTabSelected ? ['unicorn'] : [],
          selectedAssignee: 'all',
        },
      },
    });

    history.push(baseLcUrl);
  };

  const viewMessage = async (remoteUserId: string) => {
    lcMachineSend?.({
      type: 'LC_VIEW_UPDATE',
      payload: {
        viewUpdate: {
          selectedAssignee: 'all',
          selectedTags: qualifiedLeadsTabSelected ? ['unicorn'] : [],
        },
      },
    });

    setTimeout(
      () =>
        lcMachineSend?.({
          type: 'LC_REMOTE_USER_ID_CHANGE',
          payload: remoteUserId,
        }),
      50,
    );
    setTimeout(
      () => history.push(`${baseLcUrl}?remoteUserId=${remoteUserId}`),
      500,
    );
  };

  return (
    <div className={css.container}>
      <div className={css.errorsContainer}>
        <FacebookPageErrors />
      </div>

      <div className={css.channelsContainer}>
        <EducationalCardsBlock />
        <ChannelsBlock />
      </div>

      <div className={css.topContainer}>
        <div className={css.heading}>My Automation Analytics</div>
        <div className={css.topInfoContainer}>
          {screenWidth > 1200 ? (
            <div className={css.donkeyImage}>
              <DonkeyIcon />

              <div
                className={css.headerImageLink}
                onClick={() =>
                  window.open(
                    'https://mobilemonkey.com/help/l/en/article/jaknuv23cv-donkey-land-and-qualified-leads',
                    '_blank',
                  )
                }
              >
                What is Donkey Land?
              </div>
            </div>
          ) : (
            <div className={css.unicornWithFinishedStat}>
              <div className={css.headerImage}>
                <RightUnicornIcon />

                <div
                  className={css.headerImageLink}
                  onClick={() =>
                    window.open(
                      'https://mobilemonkey.com/help/l/en/article/jaknuv23cv-donkey-land-and-qualified-leads',
                      '_blank',
                    )
                  }
                >
                  What are Qualified Leads?
                </div>
              </div>
              <FinishedStats aggregateStats={aggregateStats} />
            </div>
          )}

          <div className={css.topPathContainer}>
            <div className={css.topRow}>
              {screenWidth > 1030 && <div className={css.circle} />}
              {[...Array(topArrowsCount).keys()].map(k => (
                <div className={css.pathBlock} key={k} />
              ))}
              <div
                className={css.statsBlockCickable}
                onClick={() => {
                  history.push(
                    BotBuilderRoutes.automations.getLink({
                      chatbotId: activeChatbotId,
                      view: 'my',
                    }),
                  );
                }}
              >
                <div className={css.statsBlockData}>
                  {aggregateStats.automationsCount}
                </div>
                <div className={css.statsBlockName}>Automations</div>
              </div>
              {[...Array(topArrowsCount).keys()].map(k => (
                <div className={css.pathBlock} key={k} />
              ))}
              <div
                className={css.statsBlockCickable}
                onClick={() => {
                  history.push(
                    BotBuilderRoutes.leadsContacts.getLink({
                      chatbotId: activeChatbotId,
                    }),
                  );
                }}
              >
                <div className={css.statsBlockData}>
                  {aggregateStats.contactsCount.toLocaleString(undefined)}
                </div>
                <div className={css.statsBlockName}>Contacts</div>
              </div>
              {[...Array(topArrowsCount).keys()].map(k => (
                <div className={css.pathBlock} key={k} />
              ))}
              <div
                className={css.statsBlockCickable}
                onClick={() => {
                  history.push(
                    BotBuilderRoutes.automations.getLink({
                      chatbotId: activeChatbotId,
                      view: 'my',
                    }),
                  );
                }}
              >
                <div className={css.statsBlockData}>
                  {(
                    aggregateStats.inQueueCount +
                    aggregateStats.inAutomationCount
                  ).toLocaleString(undefined)}
                </div>
                <div className={css.statsBlockName}>In Progress</div>
              </div>
              {[...Array(topArrowsCount).keys()].map(k => (
                <div className={css.pathBlock} key={k} />
              ))}
              <div
                className={css.statsBlockCickable}
                onClick={() => setIsFunnelExpanded(!isFunnelExpanded)}
              >
                <div className={css.statsBlockName}>
                  {isFunnelExpanded ? 'Collapse' : 'View my funnel'}
                </div>
                {isFunnelExpanded ? (
                  <ArrowUpOutlined className={css.arrowOutlined} />
                ) : (
                  <ArrowDownOutlined className={css.arrowOutlined} />
                )}
                {isFunnelExpanded && (
                  <div className={css.bottomRow}>
                    <div className={css.viewFunnelExpanded}>
                      <div className={css.viewFunnelExpandedTop}>
                        {/* Note: temporary commenting out till BE is ready

                      <FunnelDataBlock
                        percentage={
                          aggregateStats.messagesSentCount
                            ? (aggregateStats.engagedCount * 100) /
                              aggregateStats.messagesSentCount
                            : undefined
                        }
                        count={aggregateStats.engagedCount}
                        name="Engaged"
                      /> */}

                        <FunnelDataBlock
                          count={aggregateStats.messagesSentCount}
                          name="Messages Sent"
                        />

                        <FunnelDataBlock
                          percentage={
                            aggregateStats.messagesSentCount
                              ? (aggregateStats.unsubscribedCount * 100) /
                                aggregateStats.messagesSentCount
                              : undefined
                          }
                          count={aggregateStats.unsubscribedCount}
                          name="Unsubscribed"
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {[...Array(topArrowsCount).keys()].map(k => (
                <div className={css.pathBlock} key={k} />
              ))}
              {screenWidth > 1030 && (
                <RightOutlined className={css.statsArrow} />
              )}
            </div>
          </div>
          {screenWidth > 1200 && (
            <div className={css.unicornWithFinishedStat}>
              <FinishedStats aggregateStats={aggregateStats} />
              <div className={css.headerImage}>
                <LeftUnicornIcon />

                <div
                  className={css.headerImageLink}
                  onClick={() =>
                    window.open(
                      'https://mobilemonkey.com/help/l/en/article/jaknuv23cv-donkey-land-and-qualified-leads',
                      '_blank',
                    )
                  }
                >
                  What are Qualified Leads?
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      <div className={css.bottomContainer}>
        <div className={css.unicornInboxContainer}>
          <div className={css.heading}>Unicorn Inbox</div>
          <div
            className={classNames(css.inboxTabWrapper, {
              [css.selectedInboxTab]: selectedTab === 0,
            })}
            onClick={() => setSelectedTab(0)}
          >
            <MenuIcon
              className={css.inboxIcon}
              color={selectedTab === 0 ? '#2F54EB' : '#BFBFBF'}
            />
            All
          </div>
          <div
            className={classNames(css.inboxTabWrapper, {
              [css.selectedInboxTab]: qualifiedLeadsTabSelected,
            })}
            onClick={() => setSelectedTab(1)}
          >
            <MailTwoTone
              twoToneColor={
                qualifiedLeadsTabSelected ? '#2F54EB' : ['#BFBFBF', '#ecf0f6']
              }
              className={css.inboxIcon}
            />
            <span className={css.tabLabel}>
              Qualified Leads
              <FollowUpChats
                count={valuableConversationsCount}
                css={{ wrapper: css.followupChats }}
              />
            </span>

            <div className={css.inboxLinks} onClick={seeValuableConversations}>
              <img src={openLinkIcon} className={css.openLinkIcon} />
              Inbox
            </div>
          </div>
        </div>
        <div className={css.messagesContainer}>
          <div className={css.header}>
            {filteredConversations.length > 0 && (
              <div className={css.headerInfoTitle}>
                {qualifiedLeadsTabSelected
                  ? '🔥 Hottest New Unicorns'
                  : '🔥 Latest Conversations'}
              </div>
            )}
          </div>

          {filteredConversations.length ? (
            <>
              <div className={css.leads}>
                {filteredConversations.map((i, idx) => {
                  const { structure } = i;
                  const parsedStructure = JSONUtils.safeParse(structure);

                  let parsedMessage = 'Click Read to view the last message';

                  if (parsedStructure) {
                    const { getDisplayText } = messageStructureParser({
                      structure: ObjectUtils.deepCamelizeKeys(parsedStructure),
                    });

                    const textToDsiplay = getDisplayText();
                    if (textToDsiplay) {
                      parsedMessage = textToDsiplay;
                    }
                  }

                  const preferedName = preferredConversationName(i);

                  return (
                    <div key={idx} className={css.leadRow}>
                      <div className={css.leadMainInfo}>
                        <div className={css.leadRowIcon}>
                          <ImageWithFallback
                            src={i.profilePic}
                            fallbackImage={noProfilePicIcon}
                            alt={`${i.firstName} ${i.lastName}`}
                            className={css.leadRowIconImage}
                          />
                        </div>

                        <div className={css.leadRowInfo}>
                          <div className={css.leadRowInfoTitle}>
                            {preferedName}
                          </div>
                          <div className={css.leadRowInfoMessage}>
                            {parsedMessage}
                          </div>
                        </div>
                      </div>

                      <div className={css.leadRowAdditionalInfoContainer}>
                        <div className={css.leadRowAdditionalInfo}>
                          <MobileTwoTone
                            className={css.leadRowAdditionalInfoIcon}
                            twoToneColor="#BFBFBF"
                          />
                          {moment(Number(i.time)).calendar()}
                        </div>

                        <Button
                          color="transparent"
                          onClick={() => viewMessage(i?.remoteUserId || '')}
                          css={{
                            container: css.leadRowAdditionalActionButton,
                          }}
                        >
                          Read
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className={css.seeMore}>
                <Button
                  onClick={seeValuableConversations}
                  color="transparent"
                  css={{
                    container: css.leadRowAdditionalActionButton,
                  }}
                >
                  {qualifiedLeadsTabSelected
                    ? ' Go to Unicorn Leads'
                    : ' See All Leads'}
                  <ArrowRightOutlined className={css.seeMoreIcon} />
                </Button>
              </div>
            </>
          ) : (
            <div className={css.emptyContainer}>
              <EmptyListIcon />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
